import { debounce } from "/Scripts/tools.js";
import { getTextWithoutSign } from "/Scripts/tools.js";

/**
 * 滾動服務類別 - 提供統一的滾動相關功能
 */
export class ScrollService {
    constructor(options = {}) {
        this.options = {
            topNavSelector: ".destination_topnav",
            mobileHeaderSelector: ".header_m",
            debounceDelay: 100,
            // filter 滾動同步相關選項
            containerSelector: ".artical_wrap",
            filterSelector: ".filter_container",
            rightSelector: ".product_container",
            enableFilterSync: true,
            ...options,
        };

        this.callbacks = new Map();
        this.boundHandleScroll = null;

        // filter 滾動同步相關狀態
        this.filterSyncEnabled = this.options.enableFilterSync;
        this.lastScrollY = 0;
        this.filterSyncInitialized = false;
        this.resizeObserver = null;
    }

    /**
     * 取得導航高度
     * @param {string} selector - 導航選擇器
     * @returns {number} 導航高度
     */
    getNavHeight(selector = this.options.topNavSelector) {
        const topNav = document.querySelector(selector);
        if (!topNav) return 0;

        const isHidden = getComputedStyle(topNav).display === "none";

        if (isHidden) {
            // 暫時顯示來量高度
            const originalStyles = {
                display: topNav.style.display,
                visibility: topNav.style.visibility,
                position: topNav.style.position,
            };

            topNav.style.display = "block";
            topNav.style.visibility = "hidden";
            topNav.style.position = "absolute";

            const height = topNav.offsetHeight;

            // 還原樣式
            topNav.style.display = originalStyles.display;
            topNav.style.visibility = originalStyles.visibility;
            topNav.style.position = originalStyles.position;
            return height;
        }

        return topNav.offsetHeight;
    }

    /**
     * 等待滾動完成
     * @param {number} targetTop - 目標滾動位置
     * @param {string} behavior - 滾動行為
     * @returns {Promise} 滾動完成的 Promise
     */
    waitForScrollComplete(targetTop, behavior = "smooth") {
        return new Promise((resolve) => {
            if (behavior === "auto" || behavior === "instant") {
                // 立即滾動，直接完成
                resolve();
                return;
            }

            const tolerance = 5; // 允許的誤差範圍
            let scrollEndTimer = null;
            let lastScrollY = window.scrollY;
            let samePositionCount = 0;

            const checkScrollComplete = () => {
                const currentScrollY = window.scrollY;
                const reachedTarget = Math.abs(currentScrollY - targetTop) <= tolerance;
                const scrollStopped = Math.abs(currentScrollY - lastScrollY) < 1;

                if (scrollStopped) {
                    samePositionCount++;
                } else {
                    samePositionCount = 0;
                }

                // 滾動完成的條件：到達目標位置 或 連續3次檢測位置沒變化
                if (reachedTarget || samePositionCount >= 3) {
                    window.removeEventListener("scroll", onScroll);
                    if (scrollEndTimer) {
                        clearTimeout(scrollEndTimer);
                    }
                    resolve();
                    return;
                }

                lastScrollY = currentScrollY;

                // 重置計時器
                if (scrollEndTimer) {
                    clearTimeout(scrollEndTimer);
                }
                scrollEndTimer = setTimeout(() => {
                    window.removeEventListener("scroll", onScroll);
                    resolve();
                }, 1500); // 最長等待時間
            };

            const onScroll = () => {
                requestAnimationFrame(checkScrollComplete);
            };

            window.addEventListener("scroll", onScroll, { passive: true });

            // 立即檢查一次（如果已經在目標位置）
            requestAnimationFrame(checkScrollComplete);
        });
    }

    /**
     * 滾動到指定元素
     * @param {Element} element - 目標元素
     * @param {Object} options - 選項
     * @returns {Promise} 滾動完成的 Promise
     */
    async scrollToElement(element, options = {}) {
        if (!element) return Promise.resolve();

        const {
            padding = 0,
            behavior = "auto",
            includeNavHeight = true,
            includeMobileHeader = true,
            enableFilterSync = true, // 新增：是否啟用 filter 滾動同步
        } = options;

        let offsetTop = 0;

        if (includeNavHeight) {
            offsetTop += this.getNavHeight();
        }

        if (includeMobileHeader) {
            const headerEl = document.querySelector(
                this.options.mobileHeaderSelector
            );
            if (headerEl) {
                offsetTop += headerEl.offsetHeight;
            }
        }

        const targetTop =
            element.getBoundingClientRect().top +
            window.scrollY -
            offsetTop -
            padding;

        // 如果啟用 filter 同步且尚未初始化，先初始化
        if (enableFilterSync && !this.filterSyncInitialized) {
            this.initializeFilterSync();
        }

        // 暫時禁用 filter 同步（避免滾動時觸發同步邏輯）
        const originalSyncState = this.filterSyncEnabled;
        if (!enableFilterSync) {
            this.filterSyncEnabled = false;
        }

        window.scrollTo({
            top: targetTop,
            behavior,
        });

        // 等待滾動完成後恢復 filter 同步狀態
        if (!enableFilterSync) {
            try {
                await this.waitForScrollComplete(targetTop, behavior);
                console.log("滾動完成，恢復 filter 同步狀態");
                setTimeout(() => {
                    this.filterSyncEnabled = originalSyncState;
                }, 100);
            } catch (error) {
                console.warn("等待滾動完成時發生錯誤:", error);
                this.filterSyncEnabled = originalSyncState;
            }
        }

        return Promise.resolve();
    }

    /**
     * 按屬性查找元素
     * @param {string} selector - CSS 選擇器
     * @param {string} attributeName - 屬性名稱
     * @param {string} targetValue - 目標值
     * @returns {Element|null} 找到的元素
     */
    findElementByAttribute(selector, attributeName, targetValue) {
        const elements = document.querySelectorAll(selector);
        return Array.from(elements).find(
            (element) =>
                getTextWithoutSign(element.getAttribute(attributeName)) ===
                getTextWithoutSign(targetValue)
        );
    }

    /**
     * 檢查元素是否在視口中間
     * @param {Element} element - 要檢查的元素
     * @returns {boolean} 是否在視口中間
     */
    isElementActive(element) {
        const viewportHeight = window.innerHeight;
        const scrollPosition = window.scrollY;
        const midViewport = scrollPosition + viewportHeight / 2;

        const rect = element.getBoundingClientRect();
        const elementTop = rect.top + scrollPosition;
        const elementBottom = elementTop + rect.height;

        return elementTop <= midViewport && elementBottom >= midViewport;
    }

    /**
     * 通用元素查找函數
     * @param {string} type - 查找類型 (area|location|theme)
     * @param {Object} params - 參數
     * @returns {Element|null} 找到的元素
     */
    findElement(type, params) {
        const { target, currentLocation } = params;

        switch (type) {
            case "area":
                return this.findElementByAttribute(
                    `[${params.attr}]`,
                    params.attr,
                    target
                );

            case "location":
                return this.findElementByAttribute(
                    `[data-location-name]`,
                    "data-location-name",
                    target
                );

            case "theme":
                const locationElement = this.findElementByAttribute(
                    "[data-location-name]",
                    "data-location-name",
                    currentLocation.name
                );
                if (!locationElement) return null;

                return Array.from(
                    locationElement.querySelectorAll("[data-theme-name]")
                ).find(
                    (element) =>
                        getTextWithoutSign(element.getAttribute("data-theme-name")) ===
                        target
                );

            default:
                return null;
        }
    }

    /**
     * 設置索引並滾動
     * @param {Object} indexRef - 索引 ref
     * @param {number} newIndex - 新索引
     * @param {boolean} isClick - 是否為點擊觸發
     * @param {Object} findElementParams - 查找元素參數
     */
    setIndexWithScroll(indexRef, newIndex, isClick, findElementParams) {
        indexRef.value = newIndex;

        if (isClick && findElementParams) {
            const targetElement = this.findElement(
                findElementParams.type,
                findElementParams.params
            );

            const scrollOptions = findElementParams.scrollOptions || {};
            this.scrollToElement(targetElement, scrollOptions);
        }
    }

    /**
     * 創建滾動處理器
     * @param {Object} config - 配置選項
     * @returns {Function} 處理函數
     */
    createScrollHandler(config) {
        const {
            areas,
            isSingleArea,
            setIndex,
            setSelectedLocationIndex,
            setSelectedThemeIndex,
            selectedArea,
            selectedLocationIndex,
        } = config;

        const getLocationList = () => {
            const areaValue = selectedArea.value;
            if (!areaValue) return [];
            return areaValue.locations || areaValue.areas || [];
        };

        const getThemeList = (location) => {
            if (!location) return [];
            return location.themes || location.areas || [];
        };

        return debounce(() => {
            const viewportHeight = window.innerHeight;
            const scrollPosition = window.scrollY;
            const midViewport = scrollPosition + viewportHeight / 2;

            // 更新區域索引
            const updateAreaIndex = () => {
                const areaElements = document.querySelectorAll("[data-area-name]");
                let activeElement = areaElements[0];

                for (const element of areaElements) {
                    if (this.isElementActive(element)) {
                        const areaName = element.getAttribute("data-area-name");
                        const matchingIndex = areas.value.findIndex(
                            (area) => getTextWithoutSign(area.name) === areaName
                        );

                        if (matchingIndex !== -1) {
                            setIndex(matchingIndex, false);
                            activeElement = element;
                            break;
                        }
                    }
                }

                return activeElement;
            };

            // 更新位置索引
            const updateLocationIndex = (activeArea) => {
                if (!activeArea) return null;

                const locationElements = activeArea.querySelectorAll(
                    "[data-location-name]"
                );
                let activeLocation = null;

                for (const element of locationElements) {
                    if (this.isElementActive(element)) {
                        const locationName = element.getAttribute("data-location-name");
                        const areasToSearch = isSingleArea.value
                            ? areas.value
                            : getLocationList();

                        const matchingLocationIndex = areasToSearch?.findIndex(
                            (area) => getTextWithoutSign(area.name) === locationName
                        );

                        if (Number.isInteger(matchingLocationIndex) && matchingLocationIndex >= 0) {
                            if (isSingleArea.value) {
                                setIndex(matchingLocationIndex, false);
                            } else {
                                setSelectedLocationIndex(matchingLocationIndex, false);
                            }
                            activeLocation = element;
                            break;
                        }
                    }
                }

                return activeLocation;
            };

            // 更新主題索引
            const updateThemeIndex = (activeLocation) => {
                if (!activeLocation) return;

                const themeElements =
                    activeLocation.querySelectorAll("[data-theme-name]");

                for (const element of themeElements) {
                    if (this.isElementActive(element)) {
                        const themeName = element.getAttribute("data-theme-name");
                        const currentLocation = isSingleArea.value
                            ? selectedArea.value
                            : getLocationList()?.[selectedLocationIndex.value];

                        if (currentLocation) {
                            const matchingThemeIndex = getThemeList(currentLocation)?.findIndex(
                                (theme) => getTextWithoutSign(theme.name) === themeName
                            );

                            if (Number.isInteger(matchingThemeIndex) && matchingThemeIndex >= 0) {
                                setSelectedThemeIndex(matchingThemeIndex, false);
                                break;
                            }
                        }
                    }
                }
            };

            // 依序處理不同層級的元素
            const activeArea = updateAreaIndex();
            const activeLocation = updateLocationIndex(activeArea);
            updateThemeIndex(activeLocation);
        }, this.options.debounceDelay);
    }

    /**
     * 處理導覽列溢出效果
     * @param {Element} container - 容器元素
     */
    handleNavOverflow(container) {
        const navbar = container.querySelector(".anchor_list_wrap");
        const overflowStart = container.querySelector(".overflow_more-start");
        const overflowEnd = container.querySelector(".overflow_more-end");

        if (!navbar || !overflowStart || !overflowEnd) return;

        // 監聽滾動事件
        navbar.addEventListener("scroll", () => {
            // 左側漸層顯示邏輯
            if (navbar.scrollLeft > 0) {
                overflowStart.style.display = "block";
            } else {
                overflowStart.style.display = "";
            }

            // 右側漸層顯示邏輯
            if (navbar.scrollLeft < navbar.scrollWidth - navbar.clientWidth) {
                overflowEnd.style.display = "block";
            } else {
                overflowEnd.style.display = "";
            }
        });

        // 計算 li 總寬度
        const liList = navbar.querySelectorAll(".anchor_list_container");
        let totalLiWidth = 0;

        liList.forEach((li) => {
            totalLiWidth += li.offsetWidth;
        });

        // 根據總寬度決定是否顯示右側漸層
        if (totalLiWidth > navbar.clientWidth) {
            overflowEnd.style.display = "block";
        } else {
            overflowEnd.style.display = "";
        }
    }

    /**
     * 創建導覽列顯示/隱藏處理器
     * @param {Object} options - 選項
     * @returns {Function} 處理函數
     */
    createTopNavVisibilityHandler(options = {}) {
        const {
            topNavSelector = this.options.topNavSelector,
            topicWrapSelector = ".topic_wrap",
            topWrapSelector = ".top_wrap",
            productGridWrapSelector = ".leftsidebar-product_grid_wrap",
        } = options;

        return debounce(() => {
            const topicWrap = document.querySelector(topicWrapSelector);
            const destinationTopNav = document.querySelector(topNavSelector);

            if (!destinationTopNav) return;

            // 如果有 .topic_wrap，使用新版邏輯
            if (topicWrap) {
                const topicTop = topicWrap.getBoundingClientRect().top;
                const navHeight = destinationTopNav.offsetHeight;

                if (topicTop <= navHeight) {
                    destinationTopNav.style.display = "block";
                    destinationTopNav.style.opacity = "1";
                } else {
                    destinationTopNav.style.opacity = "0";
                    destinationTopNav.style.display = "none";
                }
            }
            // 沒有 .topic_wrap，使用舊邏輯
            else {
                const topWrap = document.querySelector(topWrapSelector);
                const productGridWrap = document.querySelector(productGridWrapSelector);

                if (!topWrap) return;

                const topWrapHeight = topWrap.offsetHeight;
                const navHeight = destinationTopNav.offsetHeight;

                // 顯示/隱藏 destinationTopNav
                if (window.scrollY > topWrapHeight - navHeight / 2) {
                    destinationTopNav.style.display = "block";
                } else {
                    destinationTopNav.style.display = "none";
                }

                // 決定透明度
                const ALLHeight = topWrapHeight + (productGridWrap?.offsetHeight || 0);
                if (window.scrollY > ALLHeight) {
                    destinationTopNav.style.opacity = "1";
                } else {
                    destinationTopNav.style.opacity = "0";
                }
            }
        }, this.options.debounceDelay);
    }

    /**
     * 添加滾動事件監聽器
     * @param {string} key - 回調識別鍵
     * @param {Function} callback - 回調函數
     */
    addScrollListener(key, callback) {
        this.callbacks.set(key, callback);
        this.updateScrollHandler();
    }

    /**
     * 移除滾動事件監聽器
     * @param {string} key - 回調識別鍵
     */
    removeScrollListener(key) {
        this.callbacks.delete(key);
        this.updateScrollHandler();
    }

    /**
     * 更新滾動處理器
     */
    updateScrollHandler() {
        if (this.boundHandleScroll) {
            window.removeEventListener("scroll", this.boundHandleScroll);
        }

        if (this.callbacks.size > 0) {
            this.boundHandleScroll = debounce(() => {
                this.callbacks.forEach((callback) => callback());
            }, this.options.debounceDelay);

            window.addEventListener("scroll", this.boundHandleScroll, {
                passive: true,
            });
        }
    }

    /**
     * 初始化 filter 滾動同步功能
     */
    initializeFilterSync() {
        if (this.filterSyncInitialized) return;

        const container = document.querySelector(this.options.containerSelector);
        const filter = document.querySelector(this.options.filterSelector);
        const right = document.querySelector(this.options.rightSelector);

        if (!container || !filter || !right) {
            console.warn("Filter sync elements not found:", {
                container: !!container,
                filter: !!filter,
                right: !!right,
            });
            return;
        }

        this.lastScrollY = window.scrollY;

        // 綁定滾動事件處理器
        this.handleFilterScroll = () => {
            if (!this.filterSyncEnabled) return;

            const currentScrollY = window.scrollY;
            const delta = currentScrollY - this.lastScrollY;

            const containerRect = container.getBoundingClientRect();

            if (containerRect.top <= 0) {
                const maxScrollTop = filter.scrollHeight - filter.clientHeight;
                const newScrollTop = filter.scrollTop + delta;

                if (
                    (delta > 0 && filter.scrollTop < maxScrollTop) ||
                    (delta < 0 && filter.scrollTop > 0)
                ) {
                    filter.scrollTop = Math.min(Math.max(newScrollTop, 0), maxScrollTop);
                }
            } else {
                filter.scrollTop = 0;
            }

            this.lastScrollY = currentScrollY;
        };

        // 添加滾動監聽器
        window.addEventListener("scroll", this.handleFilterScroll, {
            passive: true,
        });

        // 調整左側 div 高度的函數
        this.adjustLeftDivHeight = () => {
            if (right.offsetHeight < filter.offsetHeight) {
                filter.style.maxHeight = "unset";
            } else {
                filter.style.maxHeight = "";
            }
        };

        // 監聽右側容器大小變化
        this.resizeObserver = new ResizeObserver(() => {
            this.adjustLeftDivHeight();
        });

        this.resizeObserver.observe(right);

        // 頁面載入時調整一次
        window.addEventListener("load", this.adjustLeftDivHeight);

        this.filterSyncInitialized = true;
        console.log("Filter sync initialized");
    }

    /**
     * 啟用/禁用 filter 滾動同步
     * @param {boolean} enabled - 是否啟用
     */
    setFilterSyncEnabled(enabled) {
        this.filterSyncEnabled = enabled;
    }

    /**
     * 檢查 filter 滾動同步是否已啟用
     * @returns {boolean} 是否啟用
     */
    isFilterSyncEnabled() {
        return this.filterSyncEnabled;
    }

    /**
     * 手動觸發 filter 高度調整
     */
    adjustFilterHeight() {
        if (this.adjustLeftDivHeight) {
            this.adjustLeftDivHeight();
        }
    }

    /**
     * 清理 filter 滾動同步相關事件
     */
    cleanupFilterSync() {
        if (this.handleFilterScroll) {
            window.removeEventListener("scroll", this.handleFilterScroll);
            this.handleFilterScroll = null;
        }

        if (this.adjustLeftDivHeight) {
            window.removeEventListener("load", this.adjustLeftDivHeight);
            this.adjustLeftDivHeight = null;
        }

        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }

        this.filterSyncInitialized = false;
    }

    /**
     * 清理所有事件監聽器
     */
    destroy() {
        if (this.boundHandleScroll) {
            window.removeEventListener("scroll", this.boundHandleScroll);
        }
        this.callbacks.clear();
        this.cleanupFilterSync();
    }
}

// 創建默認實例
export const scrollService = new ScrollService();

// 導出個別功能函數 (向後兼容)
export const getNavHeight = (selector) => scrollService.getNavHeight(selector);
export const scrollToElement = (element, options) =>
    scrollService.scrollToElement(element, options); // 現在返回 Promise
export const waitForScrollComplete = (targetTop, behavior) =>
    scrollService.waitForScrollComplete(targetTop, behavior); // 新增
export const findElementByAttribute = (selector, attributeName, targetValue) =>
    scrollService.findElementByAttribute(selector, attributeName, targetValue);
export const isElementActive = (element) =>
    scrollService.isElementActive(element);
export const handleNavOverflow = (container) =>
    scrollService.handleNavOverflow(container);

// 新增：filter 滾動同步相關功能
export const initializeFilterSync = () => scrollService.initializeFilterSync();
export const setFilterSyncEnabled = (enabled) =>
    scrollService.setFilterSyncEnabled(enabled);
export const isFilterSyncEnabled = () => scrollService.isFilterSyncEnabled();
export const adjustFilterHeight = () => scrollService.adjustFilterHeight();
export const cleanupFilterSync = () => scrollService.cleanupFilterSync();
